import type { QueryClient } from '@tanstack/react-query'
import { clearActingUserId } from './actingUser'
import { clearAppStorageKeys } from '../lib/appStorage'
import { dismissAppFeedbackWidget } from '../lib/appFeedback/loader'
import { clearAppFeedbackToken } from './appFeedbackSession'
import { userManager } from './userManager'

/**
 * Oturum artıklarının **tek** temizlik noktası. Kurumsal çıkışta (`useLogout`) çağrılır.
 *
 * <p>Envanter ve neden bu sırayla:
 * <ol>
 *   <li><b>Uçuştaki istekler önce iptal edilir, önbellek SONRA boşaltılır.</b> Yalnız `clear()`
 *       çağrılsaydı geçiş anında uçuşta olan bir istek sonradan dönüp <b>yeni oturumun</b> önbelleğine
 *       <b>eski kullanıcının</b> verisini yazabilirdi.</li>
 *   <li><b>OIDC kullanıcısı</b> → `userManager.removeUser()`.</li>
 *   <li><b>Ekran görünüm durumu</b> → uygulama ön ekli **tüm** anahtarlar taranarak silinir
 *       (sabit liste değil — yarın eklenen bir ekran unutulmasın).</li>
 *   <li><b>Sunucu tarafı oturumu / uygulamanın kimlik çerezi</b> → <b>yok</b> (durum tutmayan belirteç
 *       modeli): temizlenecek bir şey bulunmadığından bu maddeler kod gerektirmez.</li>
 *   <li><b>Vekalet persona seçimi</b> (089) → bir oturum <b>çerezinde</b> durur, depo anahtarı değildir;
 *       bu yüzden ön ek taramasına yakalanmaz ve ayrıca silinir. Kalsaydı, aynı tarayarak giriş yapan
 *       <b>başka</b> bir kullanıcının ilk isteği önceki kişinin persona başlığını taşırdı (sunucu
 *       reddederdi ama kullanıcı sebepsiz bir hatayla karşılaşırdı).</li>
 * </ol>
 */
export interface ClearAppSessionOptions {
  /**
   * OIDC kullanıcı kaydı bu fonksiyon tarafından silinsin mi (varsayılan: **evet**).
   *
   * <p>Kurumsal çıkış yolunda **`false`** verilir: kaydı `signoutRedirect` kendisi siler ve silmeden
   * önce `id_token`'a ihtiyacı vardır (`id_token_hint` → IdP'de SSO oturumunun da kapanması). Kaydı
   * burada silmek RP-initiated logout'u sessizce bozardı (SC-005 — mevcut giriş akışında gerileme yok).
   * Envanter maddesi atlanmaz, yalnız **başka bir mekanizma** tarafından karşılanır.
   */
  removeOidcUser?: boolean
}

export async function clearAppSession(
  queryClient: QueryClient,
  options: ClearAppSessionOptions = {},
): Promise<void> {
  const { removeOidcUser = true } = options

  await queryClient.cancelQueries()
  queryClient.clear()

  if (removeOidcUser) {
    try {
      await userManager.removeUser()
    } catch {
      /* store erişilemiyorsa yoksay (temizlik best-effort) */
    }
  }

  clearAppStorageKeys(window.sessionStorage)
  clearAppStorageKeys(window.localStorage)
  clearActingUserId()

  // THY AppFeedback (096): ekranda açık kalmış bir anket, artık yürürlükte olmayan bir kimlikle
  // gönderim yapabilirdi — bileşen kendi kendine 4 dakika daha açık kalıyor. Bellekteki belirteç de
  // burada düşer; başka hiçbir yerde saklanmıyor (FR-010, FR-014).
  dismissAppFeedbackWidget()
  clearAppFeedbackToken()
}
